import React, { createContext, useContext, useEffect, useRef, useCallback } from "react";

const LiveContext = createContext(null);
export const useLive = () => useContext(LiveContext);

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WS_URL = BACKEND.replace(/^http/, "ws") + "/api/ws";

// Single app-wide WebSocket that fans real-time trade events out to any subscriber.
// Auto-reconnects with backoff; components subscribe with a callback and filter as needed.
export function LiveProvider({ children }) {
  const listeners = useRef(new Set());
  const wsRef = useRef(null);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  useEffect(() => {
    let closed = false;
    let retry = 0;
    let reconnectTimer = null;

    const clearReconnect = () => { if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; } };

    const schedule = () => {
      if (closed || document.visibilityState === "hidden") return;
      clearReconnect();
      retry = Math.min(retry + 1, 6);
      reconnectTimer = setTimeout(connect, 1000 * retry);
    };

    // Fully tear down a socket: stop ITS OWN heartbeat (ws.hb) and detach handlers so it can't
    // keep processing broadcasts or fire a stale onclose. Per-socket hb is key — a shared hb let a
    // dead socket's onclose clear the live socket's heartbeat.
    const teardown = (ws) => {
      if (!ws) return;
      if (ws.hb) { clearInterval(ws.hb); ws.hb = null; }
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try { ws.close(); } catch (e) {}
    };

    const connect = () => {
      if (closed || document.visibilityState === "hidden") return;
      const cur = wsRef.current;
      if (cur && (cur.readyState === 0 || cur.readyState === 1)) return; // already connecting/open
      // Explicitly tear down any previous socket (e.g. one left CLOSING by a background/foreground
      // during Slush signing). Without this it lingered OPEN, kept receiving every trade broadcast,
      // and each trade added another — the compounding lag that only a page reload cleared.
      teardown(cur);
      let ws;
      try { ws = new WebSocket(WS_URL); } catch (e) { schedule(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        if (wsRef.current !== ws) { teardown(ws); return; } // superseded while connecting
        retry = 0;
        ws.hb = setInterval(() => { try { ws.send("ping"); } catch (e) {} }, 25000);
      };
      ws.onmessage = (e) => {
        if (wsRef.current !== ws || document.visibilityState === "hidden") return; // ignore orphaned/bg
        let msg;
        try { msg = JSON.parse(e.data); } catch (err) { return; }
        listeners.current.forEach((fn) => { try { fn(msg); } catch (err) {} });
      };
      ws.onclose = () => {
        if (ws.hb) { clearInterval(ws.hb); ws.hb = null; }
        if (wsRef.current !== ws) return; // stale socket closing — do NOT reschedule
        schedule();
      };
      ws.onerror = () => { try { ws.close(); } catch (e) {} };
    };

    // Pause the socket entirely while the tab/app is backgrounded (common on mobile in-app
    // browsers) and reconnect on return — avoids piling up work that janks the Slush webview.
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        clearReconnect();
        teardown(wsRef.current);
        wsRef.current = null;
      } else {
        retry = 0;
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    connect();
    return () => {
      closed = true;
      clearReconnect();
      document.removeEventListener("visibilitychange", onVis);
      teardown(wsRef.current);
      wsRef.current = null;
    };
  }, []);

  return <LiveContext.Provider value={{ subscribe }}>{children}</LiveContext.Provider>;
}
